'use client'

import { usePathname } from 'next/navigation'
import { useTranslation } from 'react-i18next'

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/blocks/components/ui/select'
import i18nConfig from '@/i18nConfig'
import { buildLocaleSwitchTarget, readAlternateHref } from '@/utils/locale-switch'

const localeLabels: Record<string, string> = {
  en: 'EN',
  de: 'DE',
  ar: 'AR',
}

export default function LanguageChanger({
  supportedLocales = i18nConfig.locales,
}: {
  supportedLocales?: readonly string[]
}) {
  const { i18n } = useTranslation()
  const currentLocale = i18n.language
  const currentPathname = usePathname()

  const handleChange = (value: string) => {
    const newLocale = value

    // Persist the choice so next-i18n-router keeps the user in this language on
    // every subsequent (unprefixed) request.
    const maxAge = 60 * 60 * 24 * 365
    document.cookie = `NEXT_LOCALE=${newLocale};path=/;max-age=${maxAge};samesite=lax`

    // Content slugs differ per locale (feature 024), so a plain `/[locale]/`
    // swap carries the current language's slug into the target language and
    // lands on a not-found page. The page's own `hreflang` alternate already
    // holds the correct localized URL, so prefer it; the segment swap stays as
    // the fallback for pages that emit none.
    const target = buildLocaleSwitchTarget({
      currentPathname,
      newLocale,
      alternateHref: readAlternateHref(newLocale),
      origin: window.location.origin,
    })

    // Hard navigation (not router.push + router.refresh): applies the cookie and
    // renders the new locale in a single request. The old soft-push + refresh
    // raced with next-i18n-router re-setting NEXT_LOCALE from the OLD (Arabic)
    // URL, which flipped the user back to the previous language on the next reload.
    window.location.href = target
  }

  return (
    <Select value={currentLocale} onValueChange={handleChange}>
      <SelectTrigger aria-label='Language' className='h-8 w-[60px] gap-1 px-2 font-bold text-primary'>
        <SelectValue className='' />
      </SelectTrigger>
      <SelectContent>
        {supportedLocales.map(locale => (
          <SelectItem key={locale} value={locale}>
            {localeLabels[locale] ?? locale.toUpperCase()}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}
